import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { authApi } from '@/utils/api';

type PlanKey = 'SMALL_BUSINESS' | 'LARGE' | 'CORPORATE';

interface Tier {
  key: PlanKey;
  name: string;
  tagline: string;
  price: string;
  period: string;
  features: string[];
  highlighted?: boolean;
}

const TIERS: Tier[] = [
  {
    key: 'SMALL_BUSINESS',
    name: 'Small Business Suite',
    tagline: 'For the growing field operation',
    price: '$150',
    period: '/mo',
    features: [
      '3 field crews (3 foremen + 9 workers)',
      'Seat for every role — including Safety',
      '8 vehicles · unlimited trailers & projects',
      'All modules: JHA, audits, incidents, certs, PPE, DOT',
      'Add a crew for $50/mo (up to 2 add-ons)',
    ],
  },
  {
    key: 'LARGE',
    name: 'Large Company',
    tagline: 'Multi-crew operations & full fleets',
    price: '$397',
    period: '/mo',
    features: [
      '12 field crews (~55 staff seats)',
      'Unlimited vehicles, trailers & projects',
      'Everything in Small Business Suite',
      'Sub-accounts & AI visibility reporting',
    ],
    highlighted: true,
  },
  {
    key: 'CORPORATE',
    name: 'Corporate',
    tagline: 'Enterprise-scale, tailored',
    price: '$900',
    period: '/mo+',
    features: [
      'Unlimited crews, staff, vehicles & projects',
      'Custom software & integrations',
      'Needs-gathering consult + priority support',
    ],
  },
];

export function SignupPage() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<PlanKey>('SMALL_BUSINESS');
  const [planAddons, setPlanAddons] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // UAT-001 (user-POV 2026-10-06): browser-native validity bubbles vanish on
  // the next keystroke and never explained WHY/HOW to fix. Persistent inline
  // field errors + a summary now carry the explanation in the page body.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!companyName.trim()) errs.companyName = 'Company name is empty. How to fix: enter the business name the account is created under (e.g. Acme Tower Services).';
    if (!firstName.trim()) errs.firstName = 'First name is empty. How to fix: enter your first name — it becomes the account owner profile.';
    if (!lastName.trim()) errs.lastName = 'Last name is empty. How to fix: enter your last name — it becomes the account owner profile.';
    if (!email.trim()) {
      errs.email = 'Work email is empty. How to fix: enter the email you will sign in with (e.g. you@company.com).';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = `"${email.trim()}" is not a valid email address. How to fix: check for a missing "@", a missing domain, or stray spaces — the format must be name@domain.tld.`;
    }
    if (!password) {
      errs.password = 'Password is empty. How to fix: enter at least 8 characters.';
    } else if (password.length < 8) {
      errs.password = `Password is too short (${password.length} character${password.length === 1 ? '' : 's'}). How to fix: use at least 8 characters — longer is stronger.`;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // UAT-001: run OUR validation first and show it inline — the server would
    // reject these anyway, but the user gets the reason in place.
    if (!validate()) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    try {
      const response = await authApi.signup({
        companyName,
        firstName,
        lastName,
        email,
        password,
        plan,
        planAddons: plan === 'SMALL_BUSINESS' ? planAddons : 0,
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Signup failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-blue-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900">Start your free trial</h1>
          <p className="text-gray-600 mt-2 max-w-xl mx-auto">
            14 days, no credit card. Pick the tier that fits your operation — you can change it later.
          </p>
        </div>

        {/* Tier selection */}
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          {TIERS.map((t) => {
            // CORPORATE is assisted-only server-side (no self-serve checkout) —
            // render it as a contact-sales card, NOT a selectable plan, so a
            // customer never fills the whole form then hits a bare 'Invalid plan' 400.
            const isCorporate = t.key === 'CORPORATE';
            return isCorporate ? (
              <div
                key={t.key}
                className="text-left rounded-2xl border-2 border-gray-200 bg-white p-5 flex flex-col"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-black text-sm">{t.name}</span>
                </div>
                <div className="text-gray-500 text-xs mb-3">{t.tagline}</div>
                <div className="text-3xl font-black text-gray-900 mb-4">
                  {t.price}<span className="text-sm font-semibold text-gray-500">{t.period}</span>
                </div>
                <ul className="space-y-2 text-xs text-gray-700 mb-4 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-center text-sm font-bold rounded-lg border mt-auto pt-2 text-gray-700 border-gray-300">
                  Contact us to get started
                </div>
              </div>
            ) : (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setPlan(t.key);
                  setPlanAddons(0);
                }}
                className={`text-left rounded-2xl border-2 p-5 transition cursor-pointer flex flex-col
                  ${plan === t.key ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-black text-sm">{t.name}</span>
                  {t.highlighted && (
                    <span className="text-[10px] font-bold bg-blue-600 text-white px-2 py-0.5 rounded-full">POPULAR</span>
                  )}
                </div>
                <div className="text-gray-500 text-xs mb-3">{t.tagline}</div>
                <div className="text-3xl font-black text-gray-900 mb-4">
                  {t.price}<span className="text-sm font-semibold text-gray-500">{t.period}</span>
                </div>
                <ul className="space-y-2 text-xs text-gray-700 mb-4 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className={`text-center text-sm font-bold rounded-lg border mt-auto pt-2 ${plan === t.key ? 'text-blue-700' : 'text-gray-400'}`}>{plan === t.key ? '✓ Selected' : 'Select'}</div>
              </button>
            );
          })}
        </div>

        {/* Add-on selector (small business only) */}
        {plan === 'SMALL_BUSINESS' && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-bold text-sm">Add extra crews</div>
                <div className="text-xs text-gray-500">+$50 per crew (each = 1 foreman + 3 workers + 1 role seat + 1 vehicle)</div>
              </div>
              <div className="flex items-center gap-3">
                {[0, 1, 2].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPlanAddons(n)}
                    className={`w-10 h-10 rounded-lg border-2 font-bold transition ${
                      planAddons === n ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 text-gray-600 hover:border-blue-300'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-right text-xs text-gray-500 mt-2">
              {planAddons > 0 ? `+${planAddons * 50} crew add-on${planAddons > 1 ? 's' : ''} /mo → 3+${planAddons * 1} crews` : '3 crews included'}
            </div>
          </div>
        )}

        {/* Account form */}
        <form onSubmit={handleSubmit} noValidate className="bg-white rounded-2xl shadow-xl p-8 max-w-xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Create your account</h2>
          {/* UAT-001: persistent summary of every field that failed, in the
              page body (browser bubbles are transient and unexplained). */}
          {Object.keys(fieldErrors).length > 0 && (
            <div data-testid="signup-error-summary" role="alert" className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-semibold text-red-800">
                {Object.keys(fieldErrors).length} field{Object.keys(fieldErrors).length === 1 ? '' : 's'} need attention before the account can be created:
              </p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                {Object.entries(fieldErrors).map(([field, msg]) => (
                  <li key={field} className="text-xs text-red-800">{msg}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-5">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">Company name</label>
              <input
                id="company"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="e.g. Acme Tower Services"
              />
              {fieldErrors.companyName && <p role="alert" className="mt-1 text-xs text-red-700">{fieldErrors.companyName}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first" className="block text-sm font-medium text-gray-700 mb-1.5">First name</label>
                <input
                  id="first"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
                {fieldErrors.firstName && <p role="alert" className="mt-1 text-xs text-red-700">{fieldErrors.firstName}</p>}
              </div>
              <div>
                <label htmlFor="last" className="block text-sm font-medium text-gray-700 mb-1.5">Last name</label>
                <input
                  id="last"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
                {fieldErrors.lastName && <p role="alert" className="mt-1 text-xs text-red-700">{fieldErrors.lastName}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Work email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="you@company.com"
              />
              {fieldErrors.email && <p role="alert" className="mt-1 text-xs text-red-700">{fieldErrors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="8+ characters"
              />
              {fieldErrors.password && <p role="alert" className="mt-1 text-xs text-red-700">{fieldErrors.password}</p>}
            </div>

            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full inline-flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isLoading ? 'Creating account...' : `Start free trial`}
              {!isLoading && <ArrowRight className="h-4 w-4" />}
            </button>

            <p className="text-xs text-gray-500 text-center">
              No credit card required. Your 14-day trial starts today. Cancel anytime.
            </p>
          </div>
        </form>

        <div className="text-center mt-6">
          <span className="text-sm text-gray-500">Already have an account? </span>
          <Link to="/login" className="text-blue-600 hover:text-blue-700 text-sm font-medium">Sign in</Link>
          <span className="text-gray-300 mx-2">·</span>
          <Link to="/" className="text-blue-600 hover:text-blue-700 text-sm font-medium">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}